import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { SettingsShell } from "@/components/SettingsShell";
import { PasskeyList } from "@/components/PasskeyList";
import { Skeleton } from "@/components/Skeleton";
import { Icon } from "@/components/Icon";

/**
 * /settings/security — mirrors the SECURITY tab in designs/Settings.html:
 * two stacked panels (Passkeys, Active sessions) using .sess rows for
 * each entry.
 */
function SecuritySettings() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: queryKeys.mySessions(),
    queryFn: api.mySessions,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mySessions() }),
  });

  function formatLastSeen(lastSeen: number | null): string {
    if (lastSeen === null) return "—";
    return new Date(lastSeen).toLocaleString();
  }

  return (
    <SettingsShell>
      {/* Passkeys panel */}
      <div className="panel">
        <div className="panel-head">
          <h3>Passkeys</h3>
          <p>Sign in without a password using your devices.</p>
        </div>
        <div className="panel-body">
          <PasskeyList />
        </div>
      </div>

      {/* Active sessions panel */}
      <div className="panel">
        <div className="panel-head">
          <h3>Active sessions</h3>
          <p>Devices currently signed in to your account.</p>
        </div>
        <div className="panel-body">
          {q.isLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Skeleton height={48} />
              <Skeleton height={48} />
            </div>
          ) : q.isError ? (
            <p className="t-sm t-danger">Could not load sessions.</p>
          ) : (q.data?.sessions ?? []).length === 0 ? (
            <p className="t-sm t-muted">No active sessions found.</p>
          ) : (
            (q.data?.sessions ?? []).map((s) => (
              <div key={s.id} className="sess">
                <div className="se-ic">
                  <Icon name="grid" />
                </div>
                <div className="se-body">
                  <div className="se-nm">
                    {s.browser} · {s.os}
                  </div>
                  <div className="se-meta">
                    {s.ip ?? "—"} · {formatLastSeen(s.lastSeen)}
                  </div>
                </div>
                {s.current ? (
                  <span className="se-cur">Current</span>
                ) : (
                  <button
                    type="button"
                    className="se-act"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(s.id)}
                  >
                    Sign out
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/security")({
  component: SecuritySettings,
});
